import { useEffect, useState, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Toaster, toast } from "sonner";
import {
  Play, Stop, XCircle, WifiHigh, WifiSlash, Lightning, Gear, LockKey,
  CircleNotch, Cpu, ChartLineUp, ArrowsHorizontal, Faders, ShieldCheck, PaperPlaneTilt, List, SignOut,
} from "@phosphor-icons/react";

import { useAuth } from "@/context/AuthContext";
import { RegimeTimeline } from "@/components/RegimeTimeline";
import { GatePanel } from "@/components/GatePanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// ---- polling
const POLL_MS = 2500;

function useDashboardData() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let stopped = false;
    async function tick() {
      try {
        const res = await axios.get(`${API}/status`);
        if (!stopped) {
          setData(res.data);
          setLoading(false);
          setError(null);
        }
      } catch (e) {
        if (!stopped) {
          setError(e?.message || "network error");
          setLoading(false);
        }
      }
    }
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { stopped = true; clearInterval(id); };
  }, []);

  return { data, loading, error, setData };
}

function useLogs() {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    let stopped = false;
    async function tick() {
      try {
        const res = await axios.get(`${API}/logs?limit=200`);
        if (!stopped) setLogs(res.data.logs || []);
      } catch { /* ignore */ }
    }
    tick();
    const id = setInterval(tick, 2000);
    return () => { stopped = true; clearInterval(id); };
  }, []);
  return logs;
}

// ============================
// Number component with flash
// ============================
function FlashNumber({ value, prefix = "", suffix = "", className = "", color = null }) {
  const [flash, setFlash] = useState("");
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current !== value) {
      if (Number(value) > Number(prev.current)) setFlash("flash-profit");
      else if (Number(value) < Number(prev.current)) setFlash("flash-loss");
      const t = setTimeout(() => setFlash(""), 600);
      prev.current = value;
      return () => clearTimeout(t);
    }
  }, [value]);
  const numeric = Number(value ?? 0);
  const auto = color || (numeric > 0 ? "text-emerald-400" : numeric < 0 ? "text-red-400" : "text-slate-100");
  return (
    <span className={`font-mono ${auto} ${className} ${flash} px-1`}>{prefix}{numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{suffix}</span>
  );
}

// ============================
// STATUS DOTS
// ============================
function StatusDots({ internet, api }) {
  return (
    <div className="flex items-center gap-4" data-testid="status-dots">
      <span className="flex items-center gap-2">
        {internet ? <WifiHigh size={14} className="text-emerald-400" /> : <WifiSlash size={14} className="text-red-400" />}
        <span className="label-uppercase">Net</span>
        <span className={`dot ${internet ? "dot-on" : "dot-off"}`} data-testid="dot-internet" />
      </span>
      <span className="flex items-center gap-2">
        <Lightning size={14} className={api ? "text-emerald-400" : "text-red-400"} />
        <span className="label-uppercase">API</span>
        <span className={`dot ${api ? "dot-on" : "dot-off"}`} data-testid="dot-api" />
      </span>
    </div>
  );
}

function RegimeBadge({ regime, kill, panic, dd, eff }) {
  const map = {
    TRENDING: { color: "text-emerald-400 border-emerald-500/60 bg-emerald-950/40", label: "TRENDING" },
    RANGING:  { color: "text-cyan-300 border-cyan-500/60 bg-cyan-950/40", label: "RANGING" },
    CHOP:     { color: "text-amber-300 border-amber-500/60 bg-amber-950/40", label: "CHOP" },
    PANIC:    { color: "text-red-300 border-red-500/60 bg-red-950/40 animate-pulse", label: "PANIC" },
    UNKNOWN:  { color: "text-slate-400 border-slate-600 bg-slate-800/40", label: "SCANNING" },
  };
  const r = regime || "UNKNOWN";
  const cfg = map[r] || map.UNKNOWN;
  const showKill = kill;
  const showPanic = panic && !kill;
  return (
    <div className="flex items-center gap-2" data-testid="regime-badge">
      <span className={`label-uppercase px-2 py-1 border ${cfg.color} font-mono text-[10px] tracking-widest`}>
        {showKill ? "KILL SWITCH" : showPanic ? "PANIC COOLDOWN" : `${cfg.label}`}
      </span>
      {typeof dd === "number" && dd > 0 ? (
        <span className={`label-uppercase text-[10px] ${dd >= 5 ? "text-amber-400" : "text-slate-500"}`}>
          DD {dd.toFixed(1)}%
        </span>
      ) : null}
      {typeof eff === "number" ? (
        <span className="label-uppercase text-[10px] text-slate-500 hidden md:inline">
          R {(eff * 100).toFixed(2)}%
        </span>
      ) : null}
    </div>
  );
}

// ============================
// HEADER
// ============================
function Header({ data, onModeSwitch, onOpenApiSetup, onOpenAdmin, onOpenSidebar, onOpenLogs, onLogout, userEmail }) {
  const isLive = data?.mode === "LIVE";
  const [switching, setSwitching] = useState(false);
  const toggle = async () => {
    setSwitching(true);
    try {
      const target = isLive ? "TESTNET" : "LIVE";
      if (target === "LIVE") {
        onOpenApiSetup();
      } else {
        await onModeSwitch("TESTNET");
      }
    } finally {
      setSwitching(false);
    }
  };
  return (
    <div className="min-h-[60px] md:h-[68px] border-b border-slate-700/60 bg-[#0B1120] flex items-center px-3 md:px-5 gap-2 md:gap-6 shrink-0 flex-wrap md:flex-nowrap py-2 md:py-0" data-testid="app-header">
      {/* Left: brand + hamburger */}
      <div className="flex items-center gap-2 md:gap-3">
        <button
          onClick={onOpenSidebar}
          className="lg:hidden w-8 h-8 border border-slate-600 flex items-center justify-center text-slate-300 hover:border-cyan-500 hover:text-cyan-400"
          data-testid="btn-sidebar-open"
          aria-label="Open menu"
        >
          <List size={16} weight="bold" />
        </button>
        <div className="w-7 h-7 md:w-8 md:h-8 border border-cyan-500 flex items-center justify-center">
          <Cpu size={16} weight="bold" className="text-cyan-400" />
        </div>
        <div className="leading-tight">
          <div className="font-head font-black text-[13px] md:text-[15px] tracking-widest">CONEXUS TRADEBOT PRO</div>
          <div className="label-uppercase hidden sm:block">OKX Perpetual Swap Engine</div>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2 md:gap-4 flex-wrap">
        <div className="hidden md:flex">
          <StatusDots internet={!!data?.internet} api={!!data?.api} />
        </div>

        <RegimeBadge regime={data?.market_regime} kill={!!data?.kill_switch_triggered} panic={!!data?.panic_cooldown_active} dd={data?.drawdown_pct} eff={data?.effective_risk} />

        <div className="flex items-center gap-2 md:gap-3 border border-slate-700 px-2 md:px-3 py-1 md:py-1.5">
          <span className={`label-uppercase ${!isLive ? "text-cyan-400" : ""}`} data-testid="mode-testnet-label">Demo</span>
          <Switch
            checked={isLive}
            onCheckedChange={toggle}
            disabled={switching}
            data-testid="mode-toggle"
            className="data-[state=checked]:bg-orange-600 data-[state=unchecked]:bg-cyan-700"
          />
          <span className={`label-uppercase ${isLive ? "text-orange-400" : ""}`} data-testid="mode-live-label">Live</span>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onOpenAdmin}
          data-testid="btn-admin"
          className="border-cyan-700 h-8 bg-[#111827] hover:bg-slate-800 text-cyan-300 hidden sm:inline-flex"
        >
          <ShieldCheck size={14} className="mr-1 md:mr-2" />
          <span className="label-uppercase">Admin</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={onOpenLogs}
          data-testid="btn-logs-open"
          className="lg:hidden border-slate-600 h-8 bg-[#111827] hover:bg-slate-800"
          aria-label="Open logs"
        >
          <ChartLineUp size={14} />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={onLogout}
          data-testid="btn-logout"
          className="border-slate-600 h-8 bg-[#111827] hover:bg-slate-800"
          title={userEmail}
        >
          <SignOut size={14} className="md:mr-1" />
          <span className="label-uppercase hidden md:inline">Log out</span>
        </Button>
      </div>
    </div>
  );
}

// ============================
// ADMIN DIALOG
// ============================
function CredentialRow({ label, fieldKey, meta, meta_key, placeholder, secret = false, testid, form, setField, onReset }) {
  const isSet = meta?.[`${meta_key}_set`];
  const masked = meta?.[meta_key];
  return (
    <div className="flex flex-col gap-1 mb-3">
      <div className="flex items-center justify-between">
        <label className="label-uppercase">{label}</label>
        <div className="flex items-center gap-2">
          <span className={`label-uppercase ${isSet ? "text-emerald-400" : "text-slate-500"}`}>
            {isSet ? `set · ${masked}` : "not set"}
          </span>
          {isSet && fieldKey.startsWith("okx_") ? (
            <button
              onClick={() => onReset(fieldKey)}
              type="button"
              className="label-uppercase text-red-400 hover:text-red-300 border border-red-500/30 px-1.5 py-0.5"
              data-testid={`reset-${fieldKey}`}
              title="Clear this value"
            >Clear</button>
          ) : null}
        </div>
      </div>
      <Input
        value={form[fieldKey]}
        onChange={e => setField(fieldKey, e.target.value)}
        placeholder={placeholder}
        type={secret ? "password" : "text"}
        data-testid={testid}
        className="bg-[#0F172A] border-slate-700 h-9 font-mono text-xs"
      />
    </div>
  );
}

function AdminDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [emailSaving, setEmailSaving] = useState(false);
  const auth = useAuth();
  const [form, setForm] = useState({
    okx_demo_key: "",
    okx_demo_secret: "",
    okx_demo_passphrase: "",
    okx_live_key: "",
    okx_live_secret: "",
    okx_live_passphrase: "",
    telegram_token: "",
    telegram_chat_id: "",
    telegram_authorized_users: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/credentials`);
      setMeta(res.data);
      setForm(f => ({
        ...f,
        telegram_chat_id: res.data.telegram_chat_id || "",
        telegram_authorized_users: res.data.telegram_authorized_users || "",
      }));
    } catch {
      toast.error("Failed to load credentials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onReset = async (field) => {
    try {
      await axios.post(`${API}/admin/credentials/reset`, { field });
      toast.success(`Cleared ${field}`);
      await load();
    } catch (e) {
      toast.error(`Failed to reset ${field}`);
    }
  };

  const changePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) return toast.error("Fill all fields");
    if (pwForm.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (pwForm.new_password !== pwForm.confirm_password) return toast.error("Passwords don't match");
    setPwSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password updated. Please log in again.");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
      onOpenChange(false);
      setTimeout(async () => { await auth.logout(); window.location.href = "/login"; }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Password change failed");
    } finally {
      setPwSaving(false);
    }
  };

  const changeEmail = async () => {
    if (!emailForm.new_email || !emailForm.current_password) return toast.error("Fill all fields");
    // Simple email regex
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailForm.new_email)) return toast.error("Invalid email format");
    if (emailForm.new_email.toLowerCase() === (auth.user?.email || "").toLowerCase()) return toast.error("New email must differ from current");
    setEmailSaving(true);
    try {
      const res = await axios.post(`${API}/auth/change-email`, {
        new_email: emailForm.new_email.trim(),
        current_password: emailForm.current_password,
      });
      toast.success(`Email updated to ${res.data.email}. This is your new login.`);
      setEmailForm({ new_email: "", current_password: "" });
      // Refresh user info in AuthContext so header/badges update immediately
      if (auth?.refresh) await auth.refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email change failed");
    } finally {
      setEmailSaving(false);
    }
  };

  const testConnection = async () => {
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v && v.trim() !== "" && k.startsWith("okx_")) payload[k] = v.trim();
    });
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/test-connection`, payload);
      if (res.data.ok) {
        toast.success(`${res.data.env}: ${res.data.message}`);
      } else {
        toast.error(`${res.data.env}: ${res.data.message}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v && v.trim() !== "") payload[k] = v.trim();
    });
    if (Object.keys(payload).length === 0) {
      toast.error("Nothing to save");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/credentials`, payload);
      toast.success(`Saved ${res.data.updated.length} field(s)`);
      (res.data.warnings || []).forEach(w => toast.warning(w));
      if (res.data.api_connected) toast.success("OKX connected");
      setForm({
        okx_demo_key: "", okx_demo_secret: "", okx_demo_passphrase: "",
        okx_live_key: "", okx_live_secret: "", okx_live_passphrase: "",
        telegram_token: "",
        telegram_chat_id: form.telegram_chat_id,
        telegram_authorized_users: form.telegram_authorized_users,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="dialog-admin">
        <DialogHeader>
          <DialogTitle className="font-head tracking-wider flex items-center gap-2">
            <ShieldCheck size={18} className="text-cyan-400" />
            ADMIN — CREDENTIALS
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 label-uppercase p-6">
            <CircleNotch className="animate-spin" size={16} /> Loading ...
          </div>
        ) : (
          <Tabs defaultValue="okx" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid grid-cols-3 bg-[#0F172A] rounded-none border border-slate-700 h-9">
              <TabsTrigger value="okx" className="label-uppercase data-[state=active]:bg-slate-800/60 data-[state=active]:text-cyan-300" data-testid="admin-tab-okx">
                <Lightning size={12} className="mr-2" /> OKX
              </TabsTrigger>
              <TabsTrigger value="telegram" className="label-uppercase data-[state=active]:bg-slate-800/60 data-[state=active]:text-cyan-300" data-testid="admin-tab-telegram">
                <PaperPlaneTilt size={12} className="mr-2" /> Telegram
              </TabsTrigger>
              <TabsTrigger value="account" className="label-uppercase data-[state=active]:bg-slate-800/60 data-[state=active]:text-cyan-300" data-testid="admin-tab-account">
                <LockKey size={12} className="mr-2" /> Account
              </TabsTrigger>
            </TabsList>

            <TabsContent value="okx" className="flex-1 overflow-auto pt-4 pr-2">
              {meta?.last_api_error ? (
                <div className="mb-3 p-2 border border-red-500/40 bg-red-950/30 text-red-300 text-[11px]" data-testid="admin-conn-error">
                  <span className="label-uppercase text-red-400">Last error:</span> {String(meta.last_api_error).slice(0, 220)}
                </div>
              ) : null}
              <div className="mb-4 p-3 border border-slate-700/60 bg-[#0F172A]">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-uppercase text-cyan-400">Demo Trading</div>
                  <span className={`label-uppercase ${meta?.api_connected && meta?.current_mode === "TESTNET" ? "text-emerald-400" : "text-slate-500"}`}>
                    {meta?.api_connected && meta?.current_mode === "TESTNET" ? "● connected" : "○ disconnected"}
                  </span>
                </div>
                <CredentialRow label="Demo API Key"    fieldKey="okx_demo_key"        meta_key="okx_demo_key"        placeholder="OKX Demo API Key"        testid="admin-input-demo-key"    meta={meta} form={form} setField={setField} onReset={onReset} />
                <CredentialRow label="Demo API Secret" fieldKey="okx_demo_secret"     meta_key="okx_demo_secret"     placeholder="OKX Demo API Secret"     secret testid="admin-input-demo-secret" meta={meta} form={form} setField={setField} onReset={onReset} />
                <CredentialRow label="Demo Passphrase" fieldKey="okx_demo_passphrase" meta_key="okx_demo_passphrase" placeholder="OKX Demo Passphrase (required)" secret testid="admin-input-demo-passphrase" meta={meta} form={form} setField={setField} onReset={onReset} />
                <div className="text-[11px] text-slate-500">Get from <span className="text-cyan-400">okx.com → Demo Trading → API Management</span>. Passphrase is chosen by you at key creation and is required.</div>
              </div>
              <div className="mb-2 p-3 border border-slate-700/60 bg-[#0F172A]">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-uppercase text-orange-400">Live Trading</div>
                  <span className={`label-uppercase ${meta?.api_connected && meta?.current_mode === "LIVE" ? "text-emerald-400" : "text-slate-500"}`}>
                    {meta?.api_connected && meta?.current_mode === "LIVE" ? "● connected" : "○ disconnected"}
                  </span>
                </div>
                <CredentialRow label="Live API Key"    fieldKey="okx_live_key"        meta_key="okx_live_key"        placeholder="OKX Live API Key"        testid="admin-input-live-key" meta={meta} form={form} setField={setField} onReset={onReset} />
                <CredentialRow label="Live API Secret" fieldKey="okx_live_secret"     meta_key="okx_live_secret"     placeholder="OKX Live API Secret"     secret testid="admin-input-live-secret" meta={meta} form={form} setField={setField} onReset={onReset} />
                <CredentialRow label="Live Passphrase" fieldKey="okx_live_passphrase" meta_key="okx_live_passphrase" placeholder="OKX Live Passphrase (required)" secret testid="admin-input-live-passphrase" meta={meta} form={form} setField={setField} onReset={onReset} />
                <div className="text-[11px] text-slate-500">Enable Trade permission, restrict by IP. Only USDT-margined perpetual swaps are traded (e.g. BTC-USDT-SWAP).</div>
              </div>
            </TabsContent>

            <TabsContent value="telegram" className="flex-1 overflow-auto pt-4 pr-2">
              <div className="mb-2 p-3 border border-slate-700/60 bg-[#0F172A]">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-uppercase text-cyan-400">Telegram Bot</div>
                  <span className={`label-uppercase ${meta?.telegram_active ? "text-emerald-400" : "text-slate-500"}`}>
                    {meta?.telegram_active ? "● active" : "○ inactive"}
                  </span>
                </div>
                <CredentialRow label="Bot Token" fieldKey="telegram_token" meta_key="telegram_token" placeholder="123456:ABC-DEF..." secret testid="admin-input-tg-token" meta={meta} form={form} setField={setField} onReset={onReset} />
                <div className="flex flex-col gap-1 mb-3">
                  <label className="label-uppercase">Chat ID</label>
                  <Input value={form.telegram_chat_id} onChange={e => setField("telegram_chat_id", e.target.value)}
                    placeholder="e.g. 1904511876"
                    data-testid="admin-input-tg-chatid"
                    className="bg-[#0F172A] border-slate-700 h-9 font-mono text-xs" />
                </div>
                <div className="flex flex-col gap-1 mb-1">
                  <label className="label-uppercase">Authorized Users (comma-separated IDs)</label>
                  <Input value={form.telegram_authorized_users} onChange={e => setField("telegram_authorized_users", e.target.value)}
                    placeholder="e.g. 1904511876,987654321"
                    data-testid="admin-input-tg-users"
                    className="bg-[#0F172A] border-slate-700 h-9 font-mono text-xs" />
                </div>
                <div className="text-[11px] text-slate-500">Get token from <span className="text-cyan-400">@BotFather</span>. Send /start to your bot then check <span className="text-cyan-400">https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</span> for your chat ID.</div>
              </div>
            </TabsContent>

            <TabsContent value="account" className="flex-1 overflow-auto pt-4 pr-2">
              {/* CHANGE EMAIL */}
              <div className="mb-4 p-3 border border-slate-700/60 bg-[#0F172A]">
                <div className="label-uppercase text-cyan-400 mb-3">Change Admin Email</div>
                <div className="text-[11px] text-slate-500 mb-3">
                  Current email: <span className="text-slate-300 font-mono" data-testid="current-email">{auth?.user?.email}</span>. This becomes your new login. Password stays the same.
                </div>
                <div className="flex flex-col gap-1 mb-3">
                  <label className="label-uppercase">New Email</label>
                  <Input type="email" value={emailForm.new_email}
                    onChange={e => setEmailForm({...emailForm, new_email: e.target.value})}
                    placeholder="you@example.com"
                    data-testid="input-new-email"
                    className="bg-[#0F172A] border-slate-700 h-9 font-mono text-xs" />
                </div>
                <div className="flex flex-col gap-1 mb-3">
                  <label className="label-uppercase">Current Password (to confirm)</label>
                  <Input type="password" value={emailForm.current_password}
                    onChange={e => setEmailForm({...emailForm, current_password: e.target.value})}
                    data-testid="input-email-confirm-password"
                    className="bg-[#0F172A] border-slate-700 h-9 font-mono text-xs" />
                </div>
                <Button onClick={changeEmail} disabled={emailSaving}
                  data-testid="btn-change-email"
                  className="w-full bg-cyan-600 hover:bg-cyan-700 h-9 label-uppercase">
                  {emailSaving ? <CircleNotch className="animate-spin mr-2" /> : <LockKey size={14} className="mr-2" />}
                  Update Email
                </Button>
              </div>

              {/* CHANGE PASSWORD */}
              <div className="mb-4 p-3 border border-slate-700/60 bg-[#0F172A]">
                <div className="label-uppercase text-cyan-400 mb-3">Change Password</div>
                <div className="text-[11px] text-slate-500 mb-3">
                  Signed in as <span className="text-slate-300 font-mono">{auth?.user?.email}</span>. After changing your password you will be signed out and must log in again.
                </div>
                <div className="flex flex-col gap-1 mb-3">
                  <label className="label-uppercase">Current Password</label>
                  <Input type="password" value={pwForm.current_password}
                    onChange={e => setPwForm({...pwForm, current_password: e.target.value})}
                    data-testid="input-current-password"
                    className="bg-[#0F172A] border-slate-700 h-9 font-mono text-xs" />
                </div>
                <div className="flex flex-col gap-1 mb-3">
                  <label className="label-uppercase">New Password (min 6)</label>
                  <Input type="password" value={pwForm.new_password}
                    onChange={e => setPwForm({...pwForm, new_password: e.target.value})}
                    data-testid="input-new-password"
                    className="bg-[#0F172A] border-slate-700 h-9 font-mono text-xs" />
                </div>
                <div className="flex flex-col gap-1 mb-3">
                  <label className="label-uppercase">Confirm New Password</label>
                  <Input type="password" value={pwForm.confirm_password}
                    onChange={e => setPwForm({...pwForm, confirm_password: e.target.value})}
                    data-testid="input-confirm-password"
                    className="bg-[#0F172A] border-slate-700 h-9 font-mono text-xs" />
                </div>
                <Button onClick={changePassword} disabled={pwSaving}
                  data-testid="btn-change-password"
                  className="w-full bg-cyan-600 hover:bg-cyan-700 h-9 label-uppercase">
                  {pwSaving ? <CircleNotch className="animate-spin mr-2" /> : <LockKey size={14} className="mr-2" />}
                  Update Password
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="mt-4 shrink-0 flex flex-row gap-2 justify-between sm:justify-between">
          <Button variant="outline" onClick={testConnection} disabled={saving || loading} data-testid="btn-test-connection"
            className="border-cyan-600 text-cyan-300 bg-transparent hover:bg-cyan-950/50">
            {saving ? <CircleNotch className="animate-spin mr-2" /> : <Lightning size={14} className="mr-2" />}
            TEST CONNECTION
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-admin-cancel" className="border-slate-600 bg-transparent">Cancel</Button>
            <Button onClick={submit} disabled={saving || loading} data-testid="btn-admin-save" className="bg-cyan-600 hover:bg-cyan-700 text-white">
              {saving ? <CircleNotch className="animate-spin mr-2" /> : null}
              SAVE CREDENTIALS
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================
// API SETUP DIALOG
// ============================
function ApiSetupDialog({ open, onOpenChange, onSubmit }) {
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!key || !secret || !passphrase) {
      toast.error("API key, secret & passphrase required");
      return;
    }
    setLoading(true);
    try {
      await onSubmit(key, secret, passphrase);
      onOpenChange(false);
      setKey(""); setSecret(""); setPassphrase("");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border border-slate-700 text-slate-100 max-w-md" data-testid="dialog-api-setup">
        <DialogHeader>
          <DialogTitle className="font-head tracking-wider">SWITCH TO OKX LIVE</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-400">
            Enter your OKX <b>live</b> API key, secret &amp; passphrase. Ensure the Trade permission is enabled and IP restriction is set. Keys are stored server-side in MongoDB.
          </p>
          <div className="space-y-2">
            <label className="label-uppercase">API Key</label>
            <Input value={key} onChange={e => setKey(e.target.value)} placeholder="OKX API Key"
              className="bg-[#0F172A] border-slate-700 font-mono text-xs h-9" data-testid="input-live-key" />
          </div>
          <div className="space-y-2">
            <label className="label-uppercase">API Secret</label>
            <Input type="password" value={secret} onChange={e => setSecret(e.target.value)} placeholder="OKX API Secret"
              className="bg-[#0F172A] border-slate-700 font-mono text-xs h-9" data-testid="input-live-secret" />
          </div>
          <div className="space-y-2">
            <label className="label-uppercase">Passphrase</label>
            <Input type="password" value={passphrase} onChange={e => setPassphrase(e.target.value)} placeholder="OKX Passphrase"
              className="bg-[#0F172A] border-slate-700 font-mono text-xs h-9" data-testid="input-live-passphrase" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel-api" className="border-slate-600 bg-transparent">Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="btn-submit-api" className="bg-orange-600 hover:bg-orange-700 text-white">
            {loading ? <CircleNotch className="animate-spin mr-2" /> : null}
            ACTIVATE LIVE
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================
// METRIC CARD
// ============================
function MetricCard({ label, value, sub, prefix = "$", color = null, testid, children }) {
  return (
    <div className="card-flat px-4 py-3" data-testid={testid}>
      <div className="label-uppercase">{label}</div>
      {children ? (
        <div className="mt-1.5 text-xl font-mono font-medium">{children}</div>
      ) : (
        <div className="mt-1.5 text-xl font-medium">
          <FlashNumber value={value} prefix={prefix} color={color} className="text-[22px]" />
        </div>
      )}
      {sub ? <div className="label-uppercase mt-1">{sub}</div> : null}
    </div>
  );
}

// ============================
// CONTROLS + SETTINGS
// ============================
function SettingsInput({ label, valueKey, settings, onCommit, step = "any", type = "number" }) {
  const serverValue = settings?.[valueKey];
  const [v, setV] = useState(serverValue ?? "");
  const [focused, setFocused] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Only sync from server when: not focused, not dirty, not saving — and the value actually changed.
  useEffect(() => {
    if (focused || dirty || saving) return;
    const nextStr = serverValue === null || serverValue === undefined ? "" : String(serverValue);
    const currStr = v === null || v === undefined ? "" : String(v);
    if (nextStr !== currStr) setV(serverValue ?? "");
  }, [serverValue, focused, dirty, saving]);

  const commit = async () => {
    if (v === null || v === undefined) return;
    if (type === "number" && v === "") return;
    const coerced = type === "number" ? Number(v) : v;
    // Skip if unchanged
    if (String(coerced) === String(serverValue)) {
      setDirty(false);
      return;
    }
    setSaving(true);
    try {
      await onCommit({ [valueKey]: coerced });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const dotColor = saving ? "bg-amber-400" : dirty ? "bg-orange-400" : "bg-emerald-500/60";
  return (
    <div className="flex flex-col gap-1 mb-2">
      <label className="label-uppercase flex items-center gap-2">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} />
        {label}
      </label>
      <Input
        value={v ?? ""}
        onChange={e => { setV(e.target.value); setDirty(true); }}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); commit(); }}
        onKeyDown={e => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
        step={step}
        type={type}
        data-testid={`setting-${valueKey}`}
        className={`bg-[#0F172A] border-slate-700 h-8 font-mono text-xs px-2 ${dirty ? "border-orange-500/70" : ""}`}
      />
    </div>
  );
}

function SettingsToggle({ label, valueKey, settings, onCommit }) {
  const checked = !!settings?.[valueKey];
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="label-uppercase">{label}</span>
      <Switch
        checked={checked}
        onCheckedChange={val => onCommit({ [valueKey]: val })}
        data-testid={`toggle-${valueKey}`}
        className="data-[state=checked]:bg-cyan-500"
      />
    </div>
  );
}

// Comma / newline separated LIST editor (used for symbol whitelist)
function SettingsList({ label, valueKey, settings, onCommit, placeholder = "" }) {
  const serverValue = settings?.[valueKey];
  const serverStr = Array.isArray(serverValue) ? serverValue.join(", ") : (serverValue || "");
  const [v, setV] = useState(serverStr);
  const [focused, setFocused] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (focused || dirty || saving) return;
    if (serverStr !== v) setV(serverStr);
  }, [serverStr, focused, dirty, saving]);

  const commit = async () => {
    if (v === serverStr) { setDirty(false); return; }
    setSaving(true);
    try {
      await onCommit({ [valueKey]: v });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const dotColor = saving ? "bg-amber-400" : dirty ? "bg-orange-400" : "bg-emerald-500/60";
  const count = v.split(",").map(x => x.trim()).filter(Boolean).length;
  return (
    <div className="flex flex-col gap-1 mb-2">
      <label className="label-uppercase flex items-center gap-2">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} />
        {label} <span className="text-[9px] text-slate-500">({count})</span>
      </label>
      <textarea
        value={v ?? ""}
        onChange={e => { setV(e.target.value); setDirty(true); }}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); commit(); }}
        placeholder={placeholder}
        rows={3}
        data-testid={`setting-${valueKey}`}
        className={`bg-[#0F172A] border border-slate-700 rounded-md font-mono text-xs px-2 py-1.5 text-slate-200 resize-y focus:outline-none focus:border-cyan-500 ${dirty ? "border-orange-500/70" : ""}`}
      />
    </div>
  );
}

function Sidebar({ data, onStart, onStop, onCloseAll, onCommit, onResetDefaults, onResetSafety, running, className = "" }) {
  const settings = data?.settings || {};
  return (
    <div className={`w-full lg:w-[320px] shrink-0 border-r border-slate-700/60 overflow-y-auto bg-[#0B1120] ${className}`} data-testid="sidebar">
      <div className="p-4 border-b border-slate-700/60">
        <div className="label-uppercase mb-3">Trading Controls</div>
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={onStart}
            disabled={running}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 font-head tracking-wider text-xs col-span-1"
            data-testid="btn-start-bot"
          ><Play size={14} weight="fill" className="mr-1.5" /> START</Button>
          <Button
            onClick={onStop}
            disabled={!running}
            className="bg-red-600 hover:bg-red-700 text-white h-9 font-head tracking-wider text-xs"
            data-testid="btn-stop-bot"
          ><Stop size={14} weight="fill" className="mr-1.5" /> STOP</Button>
          <Button
            onClick={onCloseAll}
            className="bg-orange-600 hover:bg-orange-700 text-white h-9 font-head tracking-wider text-xs col-span-2"
            data-testid="btn-close-all"
          ><XCircle size={14} weight="fill" className="mr-1.5" /> CLOSE ALL POSITIONS</Button>
          <Button
            onClick={onResetDefaults}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800 h-8 label-uppercase col-span-1"
            data-testid="btn-reset-defaults"
          >Reset Settings</Button>
          <Button
            onClick={onResetSafety}
            variant="outline"
            className="border-yellow-600 text-yellow-300 hover:bg-yellow-900/30 h-8 label-uppercase col-span-1"
            data-testid="btn-reset-safety"
            title="Clear kill switch + PANIC cooldown if bot appears stuck"
          >Unstick Bot</Button>
        </div>
        {data && !data?.api ? (
          <div className="mt-2 px-2 py-1.5 border border-red-500/60 bg-red-950/30 text-[10px] text-red-300 rounded" data-testid="api-warning">
            🔌 OKX API not connected. Open Admin → OKX Keys and verify your Demo API key, Secret, and Passphrase are correct.
          </div>
        ) : null}
        {data && data?.api && Number(data?.balance) === 0 ? (
          <div className="mt-2 px-2 py-1.5 border border-red-500/60 bg-red-950/30 text-[10px] text-red-300 rounded" data-testid="balance-warning">
            ⚠ OKX balance = $0. Check your API keys, deposit demo funds, or verify the account isn&apos;t isolated-margin.
          </div>
        ) : null}
        {data?.kill_switch_triggered ? (
          <div className="mt-2 px-2 py-1.5 border border-yellow-500/60 bg-yellow-950/30 text-[10px] text-yellow-300 rounded" data-testid="kill-warning">
            🔒 Kill switch is engaged. Click <b>Unstick Bot</b> above, then Start.
          </div>
        ) : null}
        {data?.panic_cooldown_active ? (
          <div className="mt-2 px-2 py-1.5 border border-amber-500/60 bg-amber-950/30 text-[10px] text-amber-300 rounded" data-testid="panic-warning">
            ⏳ PANIC cooldown active — new entries blocked. Click <b>Unstick Bot</b> to clear.
          </div>
        ) : null}
      </div>

      <div className="px-3 py-2">
        <Accordion type="multiple" defaultValue={["core", "sltp"]} className="space-y-1">
          <AccordionItem value="core" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><Faders size={14} /> Core Risk</span>
            </AccordionTrigger>
            <AccordionContent>
              <SettingsInput label="Risk per trade (0-1)" valueKey="risk_per_trade" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Leverage" valueKey="leverage" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Max Positions" valueKey="max_positions" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Max Capital Usage %" valueKey="max_capital_usage" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Cooldown Seconds" valueKey="cooldown_seconds" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Min Hold Seconds" valueKey="minimum_hold_seconds" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Global Loss Limit" valueKey="global_loss_limit" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Margin Safety Buffer %" valueKey="margin_safety_buffer" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Position Value Cap %" valueKey="position_value_cap" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Min Stop Distance %" valueKey="minimum_stop_distance_pct" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Loop Sleep (s)" valueKey="loop_sleep" settings={settings} onCommit={onCommit} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="sltp" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><ChartLineUp size={14} /> SL / TP</span>
            </AccordionTrigger>
            <AccordionContent>
              <SettingsInput label="ATR Mult LOW vol" valueKey="atr_stop_multiplier_low_vol" settings={settings} onCommit={onCommit} />
              <SettingsInput label="ATR Mult MID vol" valueKey="atr_stop_multiplier_mid_vol" settings={settings} onCommit={onCommit} />
              <SettingsInput label="ATR Mult HIGH vol" valueKey="atr_stop_multiplier_high_vol" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Low Vol Threshold" valueKey="low_vol_threshold" settings={settings} onCommit={onCommit} />
              <SettingsInput label="High Vol Threshold" valueKey="high_vol_threshold" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Trailing ATR Mult" valueKey="trailing_atr_multiplier" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Break-Even Trigger R" valueKey="break_even_trigger_r" settings={settings} onCommit={onCommit} />
              <SettingsInput label="TP1 R" valueKey="tp1_r" settings={settings} onCommit={onCommit} />
              <SettingsInput label="TP2 R" valueKey="tp2_r" settings={settings} onCommit={onCommit} />
              <SettingsInput label="TP3 R min" valueKey="tp3_r_min" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Chandelier Period" valueKey="chandelier_period" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Chandelier Mult" valueKey="chandelier_multiplier" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Swing Lookback" valueKey="swing_lookback" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Swing ATR Buffer" valueKey="swing_atr_buffer" settings={settings} onCommit={onCommit} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="signal" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><Lightning size={14} /> Signal Filters</span>
            </AccordionTrigger>
            <AccordionContent>
              <SettingsInput label="Min Entry Score" valueKey="minimum_entry_score" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Min Trend Strength" valueKey="minimum_trend_strength" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Min RVOL" valueKey="minimum_rvol" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Max RVOL" valueKey="maximum_rvol" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Min Quote Volume" valueKey="minimum_quote_volume" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Max Candle Size" valueKey="maximum_candle_size" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Bull RSI Min" valueKey="bull_rsi_min" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Bull RSI Max" valueKey="bull_rsi_max" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Bear RSI Min" valueKey="bear_rsi_min" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Bear RSI Max" valueKey="bear_rsi_max" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Min Volatility" valueKey="minimum_volatility" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Max EMA Distance" valueKey="max_ema_distance" settings={settings} onCommit={onCommit} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="advanced" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><ArrowsHorizontal size={14} /> Advanced Filters</span>
            </AccordionTrigger>
            <AccordionContent>
              <SettingsToggle label="Multi-TF Confluence" valueKey="enable_mtf_confluence" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Correlation Filter" valueKey="enable_correlation_filter" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Funding Filter" valueKey="enable_funding_filter" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Kelly Sizing" valueKey="enable_kelly_sizing" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Squeeze Momentum" valueKey="enable_squeeze_strategy" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="VWAP Gate" valueKey="enable_vwap_gate" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Top Scan Symbols" valueKey="top_scan_symbols" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Max BTC Correlation" valueKey="max_btc_correlation" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Max Funding (bps)" valueKey="max_funding_bps" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Kelly Win Rate" valueKey="kelly_win_rate" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Kelly Avg R" valueKey="kelly_avg_r" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Kelly Cap" valueKey="kelly_cap" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Candle + ML Confluence Gate" valueKey="enable_hard_entry_gate" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Min Confluence Score (0.5-0.95)" valueKey="min_confluence_score" settings={settings} onCommit={onCommit} />
              <SettingsInput label="ML Conviction Prob" valueKey="hard_entry_ml_prob" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Min Candle Pattern Score" valueKey="hard_entry_pattern_score" settings={settings} onCommit={onCommit} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="exits" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><ShieldCheck size={14} /> Stop Loss &amp; Profit Lock</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="text-[10px] text-slate-500 mb-2" data-testid="exit-rules-note">
                Closes at −${settings?.hard_sl_usd ?? 1} loss. Once profit reaches +${settings?.profit_lock_start_usd ?? 1},
                closes when profit falls ${settings?.profit_lock_drop_usd ?? 0.5} below its peak
                {settings?.enable_fee_aware_limits ? " — measured after entry + exit fees." : " (fees not counted)."}
              </div>
              <SettingsInput label="Stop Loss ($ loss that closes trade)" valueKey="hard_sl_usd" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Stop Loss cap (% of margin, 0 = off)" valueKey="hard_sl_margin_pct" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Trailing Profit Lock" valueKey="enable_profit_lock" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Lock starts at profit ($)" valueKey="profit_lock_start_usd" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Close on drop from peak ($)" valueKey="profit_lock_drop_usd" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Fee-aware limits (count entry + exit fees)" valueKey="enable_fee_aware_limits" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Fee rate % per side (0 = read from OKX)" valueKey="fee_rate_override_pct" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Fallback fee % when OKX rate unknown" valueKey="default_fee_rate_pct" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Max fees as % of stop loss (0 = no cap)" valueKey="max_fee_share_of_sl_pct" settings={settings} onCommit={onCommit} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="learning" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><Lightning size={14} /> Self-Learning</span>
            </AccordionTrigger>
            <AccordionContent>
              <SettingsToggle label="Learn from my trades" valueKey="enable_self_learning" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Trades before a coin/strategy counts" valueKey="learner_min_samples" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Memory half-life (days)" valueKey="learner_halflife_days" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Trades before win-model trains" valueKey="learner_meta_min_trades" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Block below win rate (0-0.6)" valueKey="learner_block_win_rate" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Block below P(win) (0-0.6)" valueKey="learner_min_win_prob" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Learn from backtests (refreshed daily)" valueKey="learner_backtest_enabled" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Backtest days (7-90)" valueKey="learner_backtest_days" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Backtest trade weight vs real (0-2)" valueKey="learner_backtest_weight" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Strategy governor (auto-off losers, autonomous)" valueKey="enable_strategy_governor" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Governor: trades before judging" valueKey="governor_min_trades" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Profitable-Only mode (backtest + live positive)" valueKey="enable_profitable_only" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Daily trade pacing" valueKey="enable_trade_pacing" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Daily trade target" valueKey="daily_trade_target" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Max trades per day (0 = no cap)" valueKey="max_trades_per_day" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Pace: max entry-score relax (pts)" valueKey="pace_max_score_relax" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Pace: max confluence relax" valueKey="pace_max_conf_relax" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Pace: max candle-score relax (pts)" valueKey="pace_max_candle_relax" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Profitable-Only: min backtest trades" valueKey="profitable_only_min_backtest_trades" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Profitable-Only: real trades before live must be positive" valueKey="profitable_only_min_real_trades" settings={settings} onCommit={onCommit} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="candle-engine" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><ChartLineUp size={14} /> Candle Score Engine</span>
            </AccordionTrigger>
            <AccordionContent>
              <SettingsToggle label="Candle score strategy" valueKey="enable_candle_strategy" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Min score (of 100)" valueKey="candle_min_score" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Breakout lookback (candles)" valueKey="candle_breakout_lookback" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Stop distance (× ATR)" valueKey="candle_stop_atr" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Target distance (× ATR)" valueKey="candle_target_atr" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Max target stretch (× ATR)" valueKey="candle_max_target_atr" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Min reward:risk after costs" valueKey="candle_min_rr" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Reject if ATR above (% of price)" valueKey="candle_max_atr_pct" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Require 1h trend confirmation" valueKey="candle_require_htf" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Slippage % per fill" valueKey="slippage_pct" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Breakout strategy" valueKey="enable_breakout_strategy" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Pullback strategy" valueKey="enable_pullback_strategy" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Long trades" valueKey="enable_longs" settings={settings} onCommit={onCommit} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="autotune" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><Lightning size={14} /> Autotune (autonomous)</span>
            </AccordionTrigger>
            <AccordionContent>
              <SettingsToggle label="Autotune daily" valueKey="enable_autotune" settings={settings} onCommit={onCommit} />
              <SettingsInput label="History days" valueKey="autotune_days" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Unseen test window %" valueKey="autotune_test_pct" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Candidates tested" valueKey="autotune_candidates" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Coins used" valueKey="autotune_symbols" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Stop loss min ($)" valueKey="autotune_sl_min" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Stop loss max ($)" valueKey="autotune_sl_max" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Lock start min ($)" valueKey="autotune_lock_start_min" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Lock start max ($)" valueKey="autotune_lock_start_max" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Lock drop min ($)" valueKey="autotune_lock_drop_min" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Lock drop max ($)" valueKey="autotune_lock_drop_max" settings={settings} onCommit={onCommit} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="adaptive" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><ShieldCheck size={14} /> Adaptive &amp; Kill Switch</span>
            </AccordionTrigger>
            <AccordionContent>
              <SettingsToggle label="Adaptive Mode (Auto risk / regime)" valueKey="enable_adaptive_mode" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Kill Switch (Auto close on crash)" valueKey="enable_kill_switch" settings={settings} onCommit={onCommit} />
              <SettingsInput label="BTC 1h Drop % → Panic" valueKey="btc_panic_drop_pct_1h" settings={settings} onCommit={onCommit} />
              <SettingsInput label="BTC 1h Vol % → Block Entries" valueKey="btc_panic_vol_pct" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Drawdown % → Halve Risk" valueKey="dd_reduce_risk_pct" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Drawdown % → Kill All" valueKey="dd_kill_pct" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Panic Cooldown (sec)" valueKey="panic_cooldown_seconds" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Stale-Trade Age (hours)" valueKey="stale_trade_hours" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Stale-Trade Max R" valueKey="stale_trade_max_r" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Chop-regime Trail Multiplier" valueKey="chop_trail_multiplier" settings={settings} onCommit={onCommit} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="newstrat" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><Lightning size={14} /> New Strategies</span>
            </AccordionTrigger>
            <AccordionContent>
              <SettingsToggle label="RSI Fade (mean-reversion)" valueKey="enable_rsi_fade" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Funding-Rate Arbitrage" valueKey="enable_funding_arb" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="CVD Imbalance (volume delta)" valueKey="enable_cvd_imbalance" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Crypto-only Universe (skip stock/commodity perps)" valueKey="crypto_only_scan" settings={settings} onCommit={onCommit} />

              <div className="mt-3 pt-3 border-t border-slate-700/60">
                <SettingsToggle
                  label="🛡️ Major-Coins-Only (Symbol Whitelist)"
                  valueKey="enable_symbol_whitelist"
                  settings={settings}
                  onCommit={onCommit}
                />
                <div className="text-[9px] text-slate-500 mt-1 mb-2 leading-relaxed">
                  When ON, the bot scans/trades ONLY the coins listed below. Kills "shit coin" losses cold.
                  Accepts bases (BTC, ETH) or full instIds (BTC-USDT-SWAP), comma or newline separated.
                </div>
                <SettingsList
                  label="Symbol Whitelist"
                  valueKey="symbol_whitelist"
                  settings={settings}
                  onCommit={onCommit}
                  placeholder="BTC, ETH, SOL, BNB, XRP, ..."
                />
                <SettingsInput
                  label="Min 24h Volume ($USD)"
                  valueKey="min_24h_volume_usd"
                  settings={settings}
                  onCommit={onCommit}
                  step="1000000"
                />
                <div className="text-[9px] text-slate-500 mt-0.5 mb-2 leading-relaxed">
                  Hard floor. Anything below is rejected even if whitelisted. $50M ≈ major-only, $100M ≈ blue-chip only.
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-slate-700/60">
                <SettingsToggle
                  label="⭐ Profit Priority Mode (trade rarely, only A+ setups)"
                  valueKey="profit_priority_mode"
                  settings={settings}
                  onCommit={onCommit}
                />
                <div className="text-[9px] text-slate-500 mt-1 leading-relaxed">
                  When ON: only BREAKOUT/SQUEEZE strategies, min score {settings?.profit_priority_min_score ?? 75},
                  strict MTF alignment, rvol ≥ {settings?.profit_priority_min_rvol ?? 1.5}, max{" "}
                  {settings?.profit_priority_max_positions ?? 3} positions. Expect &lt;5 trades/week.
                </div>
                <SettingsInput label="Min Entry Score (A+ only)" valueKey="profit_priority_min_score" settings={settings} onCommit={onCommit} />
                <SettingsInput label="Min Relative Volume" valueKey="profit_priority_min_rvol" settings={settings} onCommit={onCommit} />
                <SettingsInput label="Min Trend Strength" valueKey="profit_priority_min_trend_strength" settings={settings} onCommit={onCommit} />
                <SettingsInput label="Max Concurrent Positions" valueKey="profit_priority_max_positions" settings={settings} onCommit={onCommit} />
              </div>

              <div className="mt-3 pt-3 border-t border-slate-700/60">
                <SettingsToggle
                  label="💰 Profit Lock (trailing $ floor)"
                  valueKey="enable_profit_lock"
                  settings={settings}
                  onCommit={onCommit}
                />
                <div className="text-[9px] text-slate-500 mt-1 mb-2 leading-relaxed">
                  Once PnL ≥ trigger, floor = peak − gap. Any drop below the floor closes the trade to lock the win.
                </div>
                <SettingsInput label="Start Locking at PnL $" valueKey="profit_lock_trigger_usd" settings={settings} onCommit={onCommit} step="0.5" />
                <SettingsInput label="Gap Below Peak $" valueKey="profit_lock_gap_usd" settings={settings} onCommit={onCommit} step="0.5" />

                <SettingsToggle
                  label="🛑 Zero-Tolerance Loss Stop"
                  valueKey="enable_zero_tolerance_stop"
                  settings={settings}
                  onCommit={onCommit}
                />
                <div className="text-[9px] text-slate-500 mt-1 mb-2 leading-relaxed">
                  Close any trade the moment PnL drops to this dollar value. Prevents small losses becoming big ones.
                </div>
                <SettingsInput label="Force Close at PnL $ (negative)" valueKey="zero_tolerance_loss_usd" settings={settings} onCommit={onCommit} step="0.5" />
              </div>
              <SettingsInput label="RSI Oversold Level" valueKey="rsi_fade_oversold" settings={settings} onCommit={onCommit} />
              <SettingsInput label="RSI Overbought Level" valueKey="rsi_fade_overbought" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Funding Arb Threshold (bps)" valueKey="funding_arb_bps" settings={settings} onCommit={onCommit} />
              <SettingsInput label="CVD Lookback (bars)" valueKey="cvd_lookback" settings={settings} onCommit={onCommit} />
              <SettingsInput label="CVD Imbalance Ratio" valueKey="cvd_imbalance_ratio" settings={settings} onCommit={onCommit} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="toggles" className="border-b border-slate-700/60">
            <AccordionTrigger className="label-uppercase text-cyan-400 py-2 hover:no-underline">
              <span className="flex items-center gap-2"><Gear size={14} /> Toggles &amp; Sessions</span>
            </AccordionTrigger>
            <AccordionContent>
              <SettingsToggle label="Enable Shorts" valueKey="enable_shorts" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Enable Trailing Stop" valueKey="enable_trailing_stop" settings={settings} onCommit={onCommit} />
              <SettingsToggle label="Enable Session Control" valueKey="enable_session_control" settings={settings} onCommit={onCommit} />
              <SettingsInput label="Position Mode (long_short_mode / net_mode)" valueKey="position_mode" settings={settings} onCommit={onCommit} type="text" />
              <SettingsInput label="Margin Mode (isolated / cross)" valueKey="td_mode" settings={settings} onCommit={onCommit} type="text" />
              <SettingsInput label="Entry Timeframe" valueKey="tf_entry" settings={settings} onCommit={onCommit} type="text" />
              <SettingsInput label="HTF 1 (confluence)" valueKey="tf_htf1" settings={settings} onCommit={onCommit} type="text" />
              <SettingsInput label="HTF 2 (confluence)" valueKey="tf_htf2" settings={settings} onCommit={onCommit} type="text" />
              <SettingsInput label="Session 1 Start" valueKey="session1_start" settings={settings} onCommit={onCommit} type="text" />
              <SettingsInput label="Session 1 End" valueKey="session1_end" settings={settings} onCommit={onCommit} type="text" />
              <SettingsInput label="Session 2 Start" valueKey="session2_start" settings={settings} onCommit={onCommit} type="text" />
              <SettingsInput label="Session 2 End" valueKey="session2_end" settings={settings} onCommit={onCommit} type="text" />
              <SettingsInput label="Session 3 Start" valueKey="session3_start" settings={settings} onCommit={onCommit} type="text" />
              <SettingsInput label="Session 3 End" valueKey="session3_end" settings={settings} onCommit={onCommit} type="text" />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}

// ============================
// POSITIONS TABLE
// ============================
function PositionsTable({ positions }) {
  if (!positions || positions.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-500 label-uppercase">
        No open positions
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-auto" data-testid="positions-table">
      <table className="w-full text-xs font-mono">
        <thead className="sticky top-0 bg-[#111827] z-10">
          <tr className="text-left border-b border-slate-700/60">
            <th className="label-uppercase px-4 py-2">Symbol</th>
            <th className="label-uppercase px-4 py-2">Side</th>
            <th className="label-uppercase px-4 py-2 text-right">Qty</th>
            <th className="label-uppercase px-4 py-2 text-right">Entry</th>
            <th className="label-uppercase px-4 py-2 text-right">Mark</th>
            <th className="label-uppercase px-4 py-2 text-right">PnL</th>
          </tr>
        </thead>
        <tbody>
          {positions.map((p) => (
            <tr key={p.symbol} className="border-b border-slate-800/60 hover:bg-slate-800/50" data-testid={`row-${p.symbol}`}>
              <td className="px-4 py-2 text-slate-100 font-medium">{p.symbol}</td>
              <td className="px-4 py-2">
                <span className={`px-1.5 py-0.5 text-[10px] tracking-widest border ${p.side === "LONG" ? "text-emerald-400 border-emerald-500/60" : "text-red-400 border-red-500/60"}`}>{p.side}</span>
              </td>
              <td className="px-4 py-2 text-right">{Number(p.qty).toFixed(4)}</td>
              <td className="px-4 py-2 text-right">{Number(p.entry_price).toFixed(4)}</td>
              <td className="px-4 py-2 text-right">{Number(p.mark_price).toFixed(4)}</td>
              <td className={`px-4 py-2 text-right ${p.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {p.pnl >= 0 ? "+" : ""}${Number(p.pnl).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================
// PERFORMANCE BOX
// ============================
function PerformanceBox({ data }) {
  const s = data?.settings || {};
  return (
    <div className="h-32 md:h-40 border-t border-slate-700/60 p-3 md:p-4 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 bg-[#0B1120]" data-testid="performance-box">
      <div>
        <div className="label-uppercase">Leverage</div>
        <div className="font-mono text-lg text-cyan-300">{s.leverage}x</div>
      </div>
      <div>
        <div className="label-uppercase">Risk / Trade</div>
        <div className="font-mono text-lg text-cyan-300">{(Number(s.risk_per_trade || 0) * 100).toFixed(2)}%</div>
      </div>
      <div>
        <div className="label-uppercase">Capital Used</div>
        <div className="font-mono text-lg text-cyan-300">${Number(data?.capital_used || 0).toFixed(2)}</div>
      </div>
      <div>
        <div className="label-uppercase">Realized PnL</div>
        <div className={`font-mono text-lg ${Number(data?.realized_pnl || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
          ${Number(data?.realized_pnl || 0).toFixed(2)}
        </div>
      </div>
      <div className="col-span-2 md:col-span-4 mt-2 text-[10px] md:text-[11px] text-slate-500 leading-relaxed">
        <span className="label-uppercase text-cyan-500/70">Strategy Edge:</span>{" "}
        Adaptive Regime · Kill Switch · RSI Fade · Funding Arb · CVD Imbalance · Squeeze Momentum · MTF Confluence · Chandelier Trail · Kelly Sizing · Top-80 Scanner
      </div>
    </div>
  );
}

// ============================
// AUTONOMOUS PANEL — surface /api/autonomous/* endpoints
// ============================
function AutonomousPanel() {
  const [status, setStatus] = useState(null);
  const [universe, setUniverse] = useState(null);
  const [rotations, setRotations] = useState([]);
  const [presetRuns, setPresetRuns] = useState([]);
  const [micro, setMicro] = useState(null);
  const [busy, setBusy] = useState("");
  const [topN, setTopN] = useState(200);

  const load = async () => {
    try {
      const [s, u, r, p, m] = await Promise.all([
        axios.get(`${API}/autonomous/status`),
        axios.get(`${API}/autonomous/universe`).catch(() => ({ data: null })),
        axios.get(`${API}/autonomous/rotations?limit=5`),
        axios.get(`${API}/autonomous/preset-history?limit=5`),
        axios.get(`${API}/microstructure`).catch(() => ({ data: null })),
      ]);
      setStatus(s.data);
      setUniverse(u.data);
      setRotations(r.data?.rotations || []);
      setPresetRuns(p.data?.runs || []);
      setMicro(m.data);
      if (typeof s.data?.whitelist_top_n === "number") setTopN(s.data.whitelist_top_n);
    } catch (e) {
      // silent — panel will show empty state
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const toggleAutonomous = async (enabled) => {
    setBusy("toggle");
    try {
      const res = await axios.post(`${API}/autonomous/toggle`, { enabled });
      if (res.data?.restored_fields?.length) {
        toast.success(`Autonomous OFF — restored ${res.data.restored_fields.length} manual setting(s)`);
      } else {
        toast.success(`Autonomous ${enabled ? "enabled" : "disabled"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy("");
    }
  };

  const saveTopN = async () => {
    setBusy("topn");
    try {
      const val = Math.min(500, Math.max(5, Math.round(Number(topN) || 200)));
      await axios.post(`${API}/settings`, { whitelist_top_n: val });
      toast.success(`Whitelist cap set to top ${val}`);
      await load();
    } catch (e) {
      toast.error("Failed to save cap");
    } finally {
      setBusy("");
    }
  };

  const rotateNow = async () => {
    setBusy("rotate");
    try {
      const res = await axios.post(`${API}/autonomous/rotate-whitelist`, { top_n: topN });
      toast.success(res.data?.message || "Rotation started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation failed");
    } finally {
      setBusy("");
    }
  };

  const optimizeNow = async () => {
    setBusy("optimize");
    try {
      const res = await axios.post(`${API}/autonomous/optimize-presets`, {});
      toast.success(res.data?.message || "Optimization started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Optimization failed");
    } finally {
      setBusy("");
    }
  };

  const isOn = !!status?.autonomous_mode;
  const regime = status?.regime || "UNKNOWN";
  const activeWl = status?.active_whitelist || [];

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden text-[11px] md:text-xs text-slate-300 p-3 space-y-4" data-testid="autonomous-panel">
      {/* Master toggle */}
      <div className="border border-slate-700/60 bg-slate-900/40 p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="label-uppercase text-cyan-300 mb-1">Autonomous Mode</div>
          <div className="text-slate-400 text-[10px] leading-tight">
            {isOn
              ? "Bot self-tunes ~12 preset params per detected regime. Manual overrides are locked."
              : "Bot uses your manual settings. Autonomous tuner is idle."}
          </div>
        </div>
        <Switch
          checked={isOn}
          disabled={busy === "toggle"}
          onCheckedChange={toggleAutonomous}
          data-testid="autonomous-master-toggle"
        />
      </div>

      {/* Status pills */}
      <div className="grid grid-cols-3 gap-2">
        <StatPill label="Regime" value={regime} testid="pill-regime" />
        <StatPill label="Preset Applied" value={status?.last_applied_regime || "—"} testid="pill-preset" />
        <StatPill label="Locked Fields" value={(status?.locked_fields || []).length} testid="pill-locked" />
      </div>

      {/* Universe + whitelist */}
      <div className="border border-slate-700/60 bg-slate-900/40 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="label-uppercase text-cyan-300">Trading Universe</span>
          <span className="font-mono text-slate-400">
            {universe?.count ?? "—"} coins available · {activeWl.length} in whitelist
          </span>
        </div>
        <div className="flex flex-wrap gap-1 max-h-[110px] overflow-y-auto" data-testid="active-whitelist">
          {activeWl.length === 0
            ? <span className="text-slate-500 label-uppercase">Awaiting first rotation…</span>
            : activeWl.slice(0, 60).map((s, i) => (
                <span key={i} className="border border-slate-700 text-slate-300 px-1.5 py-0.5 font-mono text-[10px]">{s}</span>
              ))}
          {activeWl.length > 60 ? (
            <span className="text-slate-500 text-[10px] px-1 py-0.5">+{activeWl.length - 60} more</span>
          ) : null}
        </div>
      </div>

      {/* Microstructure (WebSocket + orderbook imbalance) */}
      <div className="border border-slate-700/60 bg-slate-900/40 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="label-uppercase text-cyan-300">Market Microstructure</span>
          <span className={`label-uppercase text-[10px] px-2 py-0.5 border ${micro?.streamer?.connected ? "text-emerald-400 border-emerald-500/60" : "text-slate-500 border-slate-600"}`} data-testid="ws-status">
            {micro?.streamer?.connected ? "WS LIVE" : "WS IDLE"}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-1 mb-2 text-[10px]">
          <div className="border border-slate-800 p-1.5">
            <div className="text-slate-500 label-uppercase">Subs</div>
            <div className="font-mono text-slate-100">{micro?.streamer?.subscribed_count ?? 0}</div>
          </div>
          <div className="border border-slate-800 p-1.5">
            <div className="text-slate-500 label-uppercase">Prices</div>
            <div className="font-mono text-slate-100">{micro?.streamer?.prices_cached ?? 0}</div>
          </div>
          <div className="border border-slate-800 p-1.5">
            <div className="text-slate-500 label-uppercase">Books</div>
            <div className="font-mono text-slate-100">{micro?.streamer?.books_cached ?? 0}</div>
          </div>
        </div>
        <div className="text-[10px] text-slate-500 mb-1">
          Veto {(micro?.imbalance_veto_threshold ?? 0.3).toFixed(2)} · Penalty {(micro?.imbalance_penalty_threshold ?? 0.4).toFixed(2)} · Bid-ratio &lt; veto blocks LONG; &gt; (1-veto) blocks SHORT
        </div>
        <div className="max-h-[140px] overflow-y-auto" data-testid="imbalance-list">
          {Object.entries(micro?.whitelist_imbalance || {}).length === 0 ? (
            <div className="text-slate-500 label-uppercase text-[10px] py-1">Awaiting stream…</div>
          ) : Object.entries(micro.whitelist_imbalance).map(([sym, ratio]) => {
            const r = typeof ratio === "number" ? ratio : null;
            const short = sym.replace("-USDT-SWAP", "");
            const veto = micro?.imbalance_veto_threshold ?? 0.3;
            let cls = "text-slate-400";
            let lbl = "—";
            if (r !== null) {
              lbl = `${(r * 100).toFixed(1)}%`;
              if (r < veto) cls = "text-red-400";
              else if (r > 1 - veto) cls = "text-emerald-400";
              else if (r < veto + 0.10) cls = "text-amber-400";
              else if (r > 1 - veto - 0.10) cls = "text-cyan-300";
              else cls = "text-slate-300";
            }
            return (
              <div key={sym} className="flex items-center justify-between py-0.5 border-b border-slate-800/50 last:border-0 text-[11px]">
                <span className="font-mono text-slate-300">{short}</span>
                {r !== null ? (
                  <div className="flex items-center gap-2 flex-1 max-w-[60%] ml-2">
                    <div className="h-1.5 flex-1 bg-red-900/40 relative overflow-hidden">
                      <div className="h-full bg-emerald-500/60" style={{ width: `${r * 100}%` }} />
                    </div>
                    <span className={`font-mono ${cls} tabular-nums w-12 text-right`}>{lbl}</span>
                  </div>
                ) : (
                  <span className="text-slate-600 text-[10px]">no data</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Whitelist cap + manual triggers */}
      <div className="border border-slate-700/60 bg-slate-900/40 p-3 space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="label-uppercase text-cyan-300">Weekly Rotation Cap</span>
            <span className="font-mono text-slate-100">Top {topN}</span>
          </div>
          <input
            type="range"
            min={5}
            max={500}
            step={5}
            value={topN}
            onChange={(e) => setTopN(Number(e.target.value))}
            className="w-full accent-cyan-400"
            data-testid="whitelist-top-n-slider"
          />
          <div className="flex justify-between text-[10px] text-slate-500 mt-1">
            <span>5</span><span>200 (default)</span><span>500</span>
          </div>
          <button
            onClick={saveTopN}
            disabled={busy === "topn"}
            className="mt-2 w-full border border-cyan-500/40 text-cyan-300 label-uppercase px-2 py-1 hover:bg-cyan-950/40 disabled:opacity-50"
            data-testid="save-top-n"
          >
            {busy === "topn" ? "Saving…" : "Save Cap"}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={rotateNow}
            disabled={busy === "rotate"}
            className="border border-emerald-500/40 text-emerald-300 label-uppercase px-2 py-1.5 hover:bg-emerald-950/40 disabled:opacity-50"
            data-testid="btn-rotate-now"
          >
            {busy === "rotate" ? "Starting…" : "Rotate Now"}
          </button>
          <button
            onClick={optimizeNow}
            disabled={busy === "optimize"}
            className="border border-amber-500/40 text-amber-300 label-uppercase px-2 py-1.5 hover:bg-amber-950/40 disabled:opacity-50"
            data-testid="btn-optimize-now"
          >
            {busy === "optimize" ? "Starting…" : "Optimize Presets"}
          </button>
        </div>
      </div>

      {/* Rotations history */}
      <div className="border border-slate-700/60 bg-slate-900/40">
        <div className="px-3 py-2 border-b border-slate-700/60 label-uppercase text-cyan-300">
          Rotation History
        </div>
        <div className="max-h-[180px] overflow-y-auto" data-testid="rotation-history">
          {rotations.length === 0 ? (
            <div className="p-3 text-slate-500 label-uppercase">No rotations yet</div>
          ) : rotations.map((r, i) => (
            <div key={i} className="px-3 py-2 border-b border-slate-800/60 last:border-0">
              <div className="flex items-center justify-between">
                <span className="font-mono text-slate-100">{new Date(r.ts_utc).toLocaleString()}</span>
                <span className="text-slate-500">{r.runtime_seconds}s</span>
              </div>
              <div className="text-slate-400 mt-0.5">
                Kept top {r.top_n} of {r.universe_size} · Top 5: <span className="font-mono text-slate-300">{(r.top_5 || []).join(", ") || "—"}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preset optimization history */}
      <div className="border border-slate-700/60 bg-slate-900/40">
        <div className="px-3 py-2 border-b border-slate-700/60 label-uppercase text-cyan-300">
          Preset Optimization History
        </div>
        <div className="max-h-[220px] overflow-y-auto" data-testid="preset-history">
          {presetRuns.length === 0 ? (
            <div className="p-3 text-slate-500 label-uppercase">No optimizations yet</div>
          ) : presetRuns.map((run, i) => (
            <div key={i} className="px-3 py-2 border-b border-slate-800/60 last:border-0">
              <div className="flex items-center justify-between">
                <span className="font-mono text-slate-100">{new Date(run.ts_utc).toLocaleString()}</span>
                <span className="text-slate-500">{run.runtime_seconds}s</span>
              </div>
              <div className="mt-1 space-y-0.5">
                {Object.entries(run.results_summary || {}).map(([reg, r]) => (
                  <div key={reg} className="flex items-center justify-between">
                    <span className="font-mono text-slate-300">{reg}</span>
                    <span className="text-slate-400">
                      best P&L <span className="text-emerald-400">${(r.best_pnl || 0).toFixed(2)}</span>
                      {" "}· Sharpe <span className="text-slate-300">{(r.best_sharpe || 0).toFixed(2)}</span>
                      {" "}· {r.n_combos} combos
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, value, testid }) {
  return (
    <div className="border border-slate-700/60 bg-slate-900/40 p-2 text-center" data-testid={testid}>
      <div className="label-uppercase text-slate-500 text-[9px]">{label}</div>
      <div className="font-mono text-slate-100 text-xs mt-0.5 truncate">{value}</div>
    </div>
  );
}


// ============================
// LOG + HISTORY PANEL
// ============================
function LogPanel({ logs, trades, className = "" }) {
  const logRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  useEffect(() => {
    if (logRef.current && autoScroll) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);
  const onScroll = () => {
    if (!logRef.current) return;
    const el = logRef.current;
    // If user scrolled up more than 40px from bottom, pause autoscroll
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(near);
  };
  return (
    <div className={`w-full lg:w-[360px] shrink-0 border-l border-slate-700/60 flex flex-col bg-[#0B1120] min-h-0 ${className}`} data-testid="log-panel">
      <Tabs defaultValue="logs" className="flex-1 flex flex-col min-h-0">
        <TabsList className="w-full grid grid-cols-4 bg-[#111827] rounded-none border-b border-slate-700/60 h-10 shrink-0">
          <TabsTrigger value="logs" className="label-uppercase data-[state=active]:bg-slate-800/60 data-[state=active]:text-cyan-300" data-testid="tab-logs">Logs ({logs.length})</TabsTrigger>
          <TabsTrigger value="history" className="label-uppercase data-[state=active]:bg-slate-800/60 data-[state=active]:text-cyan-300" data-testid="tab-trades">Trades ({(trades || []).length})</TabsTrigger>
          <TabsTrigger value="autonomous" className="label-uppercase data-[state=active]:bg-slate-800/60 data-[state=active]:text-cyan-300" data-testid="tab-autonomous">Auto</TabsTrigger>
          <TabsTrigger value="gate" className="label-uppercase data-[state=active]:bg-slate-800/60 data-[state=active]:text-cyan-300" data-testid="tab-gate">Gate</TabsTrigger>
        </TabsList>
        <TabsContent value="logs" className="flex-1 m-0 p-1.5 md:p-2 overflow-hidden min-h-0 relative">
          <div
            ref={logRef}
            onScroll={onScroll}
            className="terminal h-full p-2 overflow-y-auto overflow-x-hidden text-[11px] md:text-xs leading-snug"
            data-testid="terminal-logs"
          >
            {(logs.length === 0) ? <div className="opacity-60">// awaiting engine output ...</div> : logs.map((l, i) => (
              <div key={i} className="whitespace-pre-wrap break-words">{`> ${l}`}</div>
            ))}
          </div>
          {!autoScroll ? (
            <button
              onClick={() => { setAutoScroll(true); if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }}
              className="absolute bottom-3 right-3 text-[10px] px-2 py-1 rounded bg-cyan-500 text-slate-950 hover:bg-cyan-400 shadow-md"
              data-testid="log-resume-scroll"
            >
              ↓ Resume
            </button>
          ) : null}
        </TabsContent>
        <TabsContent value="history" className="flex-1 m-0 p-0 overflow-hidden min-h-0">
          <div className="h-full overflow-y-auto overflow-x-hidden" data-testid="history-list">
            {(!trades || trades.length === 0) ? (
              <div className="text-slate-500 label-uppercase p-4">No trades yet</div>
            ) : trades.slice().reverse().map((t, i) => (
              <div
                key={i}
                className="border-b border-slate-800/60 px-3 py-2 grid grid-cols-[minmax(0,1fr)_auto] gap-2 items-center hover:bg-slate-800/50"
              >
                <div className="flex flex-col min-w-0">
                  <span className="font-mono text-[11px] md:text-xs text-slate-100 truncate">{t.symbol}</span>
                  <span className="label-uppercase text-[9px] md:text-[10px] truncate">
                    {t.side} · {t.reason} · {t.time}
                  </span>
                </div>
                <div className={`font-mono text-xs md:text-sm shrink-0 tabular-nums ${t.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {t.pnl >= 0 ? "+" : ""}${Number(t.pnl).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="autonomous" className="flex-1 m-0 p-0 overflow-hidden min-h-0">
          <AutonomousPanel />
        </TabsContent>
        <TabsContent value="gate" className="flex-1 m-0 p-0 overflow-hidden min-h-0">
          <GatePanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================
// TRADING DASHBOARD (authenticated)
// ============================
function TradingDashboard() {
  const { data, loading, error } = useDashboardData();
  const logs = useLogs();
  const [apiSetupOpen, setApiSetupOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const auth = useAuth();

  const running = data?.status === "RUNNING";
  const isLive = data?.mode === "LIVE";
  const [adminOpen2, setAdminOpen2] = useState(false); // placeholder (unused, kept for compat)

  const commitSettings = async (payload) => {
    try {
      const res = await axios.post(`${API}/settings`, payload);
      const rejected = res.data?.rejected || [];
      if (rejected.length) {
        rejected.forEach((r) => toast.error(`${r.field}: ${r.value} out of range [${r.bounds[0]} – ${r.bounds[1]}]`));
      } else {
        toast.success("Settings updated");
      }
    } catch (e) {
      toast.error("Failed to update settings");
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset ALL trading parameters to code defaults? Credentials & bot state are NOT affected.")) return;
    try {
      const res = await axios.post(`${API}/settings/reset`);
      toast.success(`Reset ${res.data.reset.length} settings to defaults`);
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const startBot = async () => {
    try {
      await axios.post(`${API}/bot/start`);
      toast.success(`Bot started (${data?.mode})`);
    } catch { toast.error("Failed to start bot"); }
  };
  const stopBot = async () => {
    try {
      await axios.post(`${API}/bot/stop`);
      toast(`Bot stopped`);
    } catch { toast.error("Failed to stop bot"); }
  };
  const closeAll = async () => {
    try {
      await axios.post(`${API}/positions/close-all`);
      toast("Closing all positions ...");
    } catch { toast.error("Failed to close positions"); }
  };
  const resetSafety = async () => {
    try {
      const res = await axios.post(`${API}/bot/reset-safety`);
      if (res.data?.ok) {
        toast.success("Safety locks cleared. Click Start to trade again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };
  const switchMode = async (mode, live_api_key = null, live_api_secret = null, live_passphrase = null) => {
    try {
      const res = await axios.post(`${API}/mode`, { mode, live_api_key, live_api_secret, live_passphrase });
      toast.success(res.data.message || `Mode switched to ${mode}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to switch mode");
    }
  };
  const submitLiveKeys = async (k, s, p) => switchMode("LIVE", k, s, p);

  return (
    <div className="App flex flex-col h-screen overflow-hidden">
      <Toaster richColors position="top-right" theme="dark" />
      <Header
        data={data}
        onModeSwitch={switchMode}
        onOpenApiSetup={() => setApiSetupOpen(true)}
        onOpenAdmin={() => setAdminOpen(true)}
        onOpenSidebar={() => setSidebarOpen(true)}
        onOpenLogs={() => setLogsOpen(true)}
        onLogout={async () => { await auth.logout(); }}
        userEmail={auth.user?.email}
      />
      <ApiSetupDialog open={apiSetupOpen} onOpenChange={setApiSetupOpen} onSubmit={submitLiveKeys} />
      <AdminDialog open={adminOpen} onOpenChange={setAdminOpen} />

      {/* Mobile status dots row */}
      <div className="md:hidden flex items-center justify-between border-b border-slate-700/60 px-3 py-2 bg-[#0B1120]">
        <StatusDots internet={!!data?.internet} api={!!data?.api} />
        <button onClick={() => setAdminOpen(true)} className="border border-cyan-700 text-cyan-300 label-uppercase px-2 py-1" data-testid="btn-admin-mobile">
          <ShieldCheck size={12} className="inline mr-1" /> Admin
        </button>
      </div>

      {data?.region_blocked ? (
        <div className="bg-orange-900/40 border-b border-orange-500/60 text-orange-200 px-3 md:px-5 py-2 text-xs flex flex-wrap items-center gap-2 md:gap-3" data-testid="region-blocked-banner">
          <Lightning size={14} weight="fill" className="text-orange-400" />
          <span className="label-uppercase text-orange-300">Region Blocked</span>
          <span className="flex-1 min-w-[200px]">OKX rejects this server&apos;s IP. Deploy this app on a VPS in a supported region.</span>
        </div>
      ) : null}

      {data?.account_setup_required ? (
        <div className="bg-amber-900/40 border-b border-amber-500/60 text-amber-200 px-3 md:px-5 py-2 text-xs flex flex-wrap items-center gap-2 md:gap-3" data-testid="account-setup-banner">
          <Gear size={14} weight="fill" className="text-amber-400" />
          <span className="label-uppercase text-amber-300">Setup Required</span>
          <span className="flex-1 min-w-[200px]">{data.account_setup_msg}</span>
          <a
            href="https://www.okx.com/trade-demo"
            target="_blank"
            rel="noreferrer"
            className="label-uppercase text-cyan-300 border border-cyan-500/40 px-2 py-0.5 hover:bg-cyan-950/50"
            data-testid="link-okx-demo"
          >Open OKX Demo →</a>
        </div>
      ) : null}

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar — fixed on desktop */}
        <Sidebar
          data={data}
          running={running}
          onStart={startBot}
          onStop={stopBot}
          onCloseAll={closeAll}
          onCommit={commitSettings}
          onResetDefaults={resetDefaults}
          onResetSafety={resetSafety}
          className="hidden lg:block"
        />

        {/* Sidebar — drawer on mobile */}
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="w-[85vw] max-w-[340px] bg-[#0B1120] border-slate-700 p-0 overflow-hidden flex flex-col">
            <SheetHeader className="p-3 border-b border-slate-700/60">
              <SheetTitle className="font-head tracking-widest text-slate-100 text-sm">CONTROLS &amp; SETTINGS</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto">
              <Sidebar
                data={data}
                running={running}
                onStart={startBot}
                onStop={stopBot}
                onCloseAll={closeAll}
                onCommit={commitSettings}
                onResetDefaults={resetDefaults}
          onResetSafety={resetSafety}
              />
            </div>
          </SheetContent>
        </Sheet>

        {/* Main */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-700/50 border-b border-slate-700/60">
            <MetricCard label="Account Balance" value={data?.balance} testid="metric-balance" color="text-slate-100" />
            <MetricCard label="Live PnL" value={data?.daily_pnl} testid="metric-live-pnl" />
            <MetricCard label="Bot Status" testid="metric-status">
              <span className={`inline-flex items-center gap-2 px-2 py-0.5 border ${running ? "text-emerald-400 border-emerald-500/50" : "text-slate-400 border-slate-600"}`}>
                {running ? <Play size={14} weight="fill" /> : <Stop size={14} weight="fill" />}
                {running ? "RUNNING" : "STOPPED"}
              </span>
            </MetricCard>
            <MetricCard label="Mode" testid="metric-mode">
              <span className={`inline-flex items-center gap-2 px-2 py-0.5 border ${isLive ? "text-orange-400 border-orange-500/50" : "text-cyan-300 border-cyan-500/50"}`}>
                <Lightning size={14} weight="fill" />
                {data?.mode || "—"}
              </span>
            </MetricCard>
          </div>

          {/* Positions */}
          <div className="flex-1 flex flex-col overflow-hidden bg-[#0B1120]">
            <div className="px-3 md:px-4 py-2 border-b border-slate-700/60 flex items-center justify-between">
              <span className="label-uppercase text-cyan-300">Live Open Positions</span>
              <span className="label-uppercase">{data?.positions?.length ?? 0} active</span>
            </div>
            <PositionsTable positions={data?.positions || []} />
            <RegimeTimeline />
            <PerformanceBox data={data} />
          </div>
        </div>

        {/* Logs — fixed on desktop */}
        <LogPanel logs={logs} trades={data?.trade_history || []} className="hidden lg:flex" />

        {/* Logs — drawer on mobile */}
        <Sheet open={logsOpen} onOpenChange={setLogsOpen}>
          <SheetContent side="right" className="w-[85vw] max-w-[380px] bg-[#0B1120] border-slate-700 p-0 flex flex-col">
            <SheetHeader className="p-3 border-b border-slate-700/60">
              <SheetTitle className="font-head tracking-widest text-slate-100 text-sm">LOGS &amp; TRADE HISTORY</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-hidden">
              <LogPanel logs={logs} trades={data?.trade_history || []} />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-slate-500 label-uppercase flex items-center gap-2">
            <CircleNotch className="animate-spin" size={16} />
            Connecting to engine ...
          </div>
        </div>
      ) : null}
      {error ? <div className="absolute bottom-2 left-2 text-xs text-red-400" data-testid="conn-error">Connection: {error}</div> : null}
    </div>
  );
}

export { TradingDashboard, API, BACKEND_URL };
export default TradingDashboard;
